import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../../components/MainLayout';
import {
  CheckSquare,
  Navigation,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  UserCheck,
  Paperclip,
  Eye,
  Download,
  AlertCircle,
  ChevronRight,
  ArrowLeft,
  MapPin,
  Building2,
  Users,
  ShieldCheck,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

import TravelRequestDetailsModal from './TravelRequestDetailsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatusBadge = ({ status, approvalStatus }) => {
  const st = (status || 'DRAFT').toUpperCase();
  const appSt = (approvalStatus || '').toLowerCase();

  let meta = { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  if (st === 'PAID') {
    meta = { label: 'Paid / Disbursed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
  } else if (st === 'REJECTED' || appSt === 'rejected') {
    meta = { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300' };
  } else if (st === 'PAYMENT_PENDING' || appSt === 'approved') {
    meta = { label: 'Fully Approved — Payment Pending', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' };
  } else if (st === 'DRAFT') {
    meta = { label: 'Returned to Submitter / Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  } else {
    meta = { label: 'Submitted — In Workflow', cls: 'bg-amber-100 text-amber-900 border-amber-300' };
  }

  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

export default function LocalTravelApprovalInbox({ isEmbedded = false }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [inboxData, setInboxData] = useState({
    pending: [],
    approved: [],
    rejected: [],
    returned: [],
    history: [],
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [actionRemarks, setActionRemarks] = useState({});
  const [actionProcessing, setActionProcessing] = useState(null);

  // Detail Modal State
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const fetchInbox = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/ess/local-travel/approval-inbox`, { withCredentials: true });
      setInboxData({
        pending: data.pending || [],
        approved: data.approved || [],
        rejected: data.rejected || [],
        returned: data.returned || [],
        history: data.history || [],
      });
    } catch (err) {
      console.error('Failed to load Local Conveyance Approval Inbox:', err);
      toast.error('Failed to load Local Conveyance approval inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  const handleRemarkChange = (id, text) => {
    setActionRemarks((prev) => ({ ...prev, [id]: text }));
  };

  const handleApprove = async (reportId) => {
    try {
      setActionProcessing(reportId);
      const remarks = actionRemarks[reportId] || 'Approved';
      await axios.post(
        `${API_URL}/ess/local-travel/finance/${reportId}/approve`,
        { approval_remarks: remarks, remarks },
        { withCredentials: true }
      );
      toast.success('Local Conveyance claim approved successfully!');
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve claim');
    } finally {
      setActionProcessing(null);
    }
  };

  const handleReject = async (reportId) => {
    const remarks = actionRemarks[reportId] || '';
    if (!remarks.trim()) {
      toast.error('Please enter a rejection reason in the remarks field.');
      return;
    }
    try {
      setActionProcessing(reportId);
      await axios.post(
        `${API_URL}/ess/local-travel/finance/${reportId}/reject`,
        { rejection_reason: remarks, remarks },
        { withCredentials: true }
      );
      toast.success('Local Conveyance claim rejected.');
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject claim');
    } finally {
      setActionProcessing(null);
    }
  };

  const handleSendBack = async (reportId) => {
    const remarks = actionRemarks[reportId] || '';
    if (!remarks.trim()) {
      toast.error('Please enter remarks explaining why the claim is being returned.');
      return;
    }
    try {
      setActionProcessing(reportId);
      await axios.post(
        `${API_URL}/ess/local-travel/finance/${reportId}/send-back`,
        { remarks },
        { withCredentials: true }
      );
      toast.success('Claim returned to employee for correction.');
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to return claim');
    } finally {
      setActionProcessing(null);
    }
  };

  const currentTabItems = inboxData[activeTab] || [];
  const filteredItems = currentTabItems.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.report_number || '').toLowerCase().includes(q) ||
      (item.employee_name || '').toLowerCase().includes(q) ||
      (item.employee_code || '').toLowerCase().includes(q) ||
      (item.department_name || '').toLowerCase().includes(q) ||
      (item.travel_purpose || '').toLowerCase().includes(q) ||
      (item.from_location || '').toLowerCase().includes(q) ||
      (item.to_location || '').toLowerCase().includes(q)
    );
  });

  const content = (
    <div className="space-y-6 max-w-[1600px] mx-auto font-sans" data-testid="local-conveyance-approval-inbox">
      {/* Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30 shrink-0">
            <Navigation className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight">Local Conveyance Approval Inbox</h1>
            <p className="text-xs text-blue-200 mt-0.5">
              Multi-level Local Conveyance approval workflow · Manager &amp; Finance Review
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchInbox}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-bold text-white transition-all"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs & Search Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {[
            { id: 'pending', label: 'Pending Requests', count: inboxData.pending.length, color: 'bg-amber-500 text-white' },
            { id: 'approved', label: 'Approved Requests', count: inboxData.approved.length, color: 'bg-emerald-500 text-white' },
            { id: 'rejected', label: 'Rejected Requests', count: inboxData.rejected.length, color: 'bg-rose-500 text-white' },
            { id: 'returned', label: 'Returned Requests', count: inboxData.returned.length, color: 'bg-slate-500 text-white' },
            { id: 'history', label: 'History', count: inboxData.history.length, color: 'bg-blue-500 text-white' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Search by Employee, Code, Purpose, Route..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Main List */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading approval inbox...</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-16 text-center text-slate-400 font-semibold italic space-y-2">
            <AlertCircle className="w-8 h-8 text-slate-300 mx-auto" />
            <p>No {activeTab} Local Conveyance requests found.</p>
          </div>
        ) : (
          filteredItems.map((r) => {
            const reportId = r.id || r._id;
            const grandTotal = r.total_amount || r.totals?.grand_total || 0;
            const eligibleTotal = r.eligible_amount || r.totals?.eligible_total || grandTotal;

            // Connected Route Flow
            const segments = r.segments || [];
            const routePoints = [];
            if (segments.length > 0 && segments[0].from_location) routePoints.push(segments[0].from_location);
            segments.forEach((s) => {
              if (s.to_location) routePoints.push(s.to_location);
            });
            if (!routePoints.length) {
              if (r.from_location) routePoints.push(r.from_location);
              if (r.to_location) routePoints.push(r.to_location);
            }
            const connectedRoute = routePoints.join(' → ') || 'Local Duty Route';

            // Expenses Breakdown
            const expDoc = r.expenses || {};
            const foodAmt = r.food_amount || expDoc.food_expense || 0;
            const parkingTollAmt = r.parking_toll_amount || (expDoc.parking || 0) + (expDoc.toll || 0);
            const conveyanceAmt = r.conveyance_amount || grandTotal - (foodAmt + parkingTollAmt + (r.other_amount || 0));
            const otherAmt = r.other_amount || expDoc.other_expense || 0;

            const approvals = r.approvals || [];
            const currIdx = r.current_level_index;
            const isProcessing = actionProcessing === reportId;

            return (
              <div key={reportId} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4 shadow-sm hover:border-slate-300 transition-all">
                {/* Card Top Header */}
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded border border-blue-200">
                        {r.report_number || r.report_id || `LC-${reportId.slice(-6).toUpperCase()}`}
                      </span>
                      <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" /> Date: <b>{r.report_date}</b>
                      </span>
                      {r.requested_on_behalf && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-900 border border-indigo-300 font-bold px-2 py-0.5 rounded-md">
                          On-Behalf Claim
                        </span>
                      )}
                    </div>
                    <h4 className="font-bold text-slate-900 text-base mt-1 flex items-center gap-2">
                      {r.employee_name} ({r.employee_code || 'EMP'}) — <span className="text-blue-700">{r.travel_purpose || 'Local Conveyance'}</span>
                    </h4>
                    <p className="text-xs text-slate-500">
                      Department: <b>{r.department_name || 'General'}</b> &bull; Designation: <b>{r.designation_name || 'Staff'}</b> &bull; Company: <b>{r.company_name || 'Main'}</b>
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={r.status} approvalStatus={r.approval_status} />
                    <span className="bg-amber-100 text-amber-900 text-xs font-bold px-3 py-1 rounded-full border border-amber-300">
                      Current Stage: {
                        approvals && currIdx !== null && currIdx !== undefined && approvals[currIdx]
                          ? (approvals[currIdx].level_name || approvals[currIdx].approver_role || `Level ${currIdx + 1}`)
                          : (r.approval_workflow_name || 'Review')
                      }
                    </span>
                    <button
                      onClick={() => {
                        setSelectedReportId(reportId);
                        setDetailsModalOpen(true);
                      }}
                      className="px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl text-xs font-bold border border-blue-200 flex items-center gap-1.5 transition-all shadow-sm"
                    >
                      <Eye className="w-3.5 h-3.5 text-blue-600" /> View Full Details
                    </button>
                    <button
                      onClick={() => {
                        window.open(`${API_URL}/ess/local-travel/reports/${reportId}/pdf`, '_blank');
                      }}
                      className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold border border-slate-300 flex items-center gap-1.5 transition-all shadow-sm"
                    >
                      <Download className="w-3.5 h-3.5 text-slate-600" /> Download PDF
                    </button>
                  </div>
                </div>

                {/* Workflow Stepper Bar */}
                {approvals.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 text-xs">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mr-1">Approval Levels:</span>
                    {approvals.map((lvl, idx) => {
                      const isDone = lvl.status === 'approved';
                      const isCurrent = currIdx === idx;
                      const isRejected = lvl.status === 'rejected';
                      return (
                        <div
                          key={idx}
                          className={`px-3 py-1 rounded-lg text-xs font-bold border flex items-center gap-1.5 ${
                            isDone
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                              : isCurrent
                              ? 'bg-blue-100 text-blue-900 border-blue-400 font-extrabold shadow-sm'
                              : isRejected
                              ? 'bg-rose-50 text-rose-800 border-rose-300'
                              : 'bg-slate-100 text-slate-500 border-slate-200'
                          }`}
                        >
                          <span>L{idx + 1}: {lvl.level_name || lvl.approver_role}</span>
                          {lvl.approver_name && <span className="text-[10px] text-slate-600 font-normal">({lvl.approver_name})</span>}
                          {isDone && <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                          {isRejected && <XCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Connected Route & Distance Grid */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 space-y-2 text-xs">
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <span className="font-bold text-slate-800 flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-blue-600" /> Linked Route Flow: <span className="text-blue-900 font-semibold">{connectedRoute}</span>
                    </span>
                    <span className="font-bold text-slate-700 bg-slate-100 px-2.5 py-0.5 rounded border border-slate-200">
                      Total KM: {r.total_km || 0} KM (Odometer: {r.start_odometer || 0} → {r.end_odometer || 0})
                    </span>
                  </div>
                </div>

                {/* Expense Breakdown 4 Categories Table */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 text-xs space-y-2">
                  <div className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">Itemized Expense Breakdown</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-slate-700">
                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Conveyance Fare</span>
                      <b className="text-sm font-mono text-slate-900">₹ {conveyanceAmt.toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Food &amp; Refreshment</span>
                      <b className="text-sm font-mono text-slate-900">₹ {foodAmt.toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Parking &amp; Toll</span>
                      <b className="text-sm font-mono text-slate-900">₹ {parkingTollAmt.toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Other Expenses</span>
                      <b className="text-sm font-mono text-slate-900">₹ {otherAmt.toLocaleString('en-IN')}</b>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                    <span className="font-bold text-slate-800">Grand Total Claim Amount</span>
                    <div className="text-right">
                      <span className="text-base font-black text-blue-900 font-mono">₹ {grandTotal.toLocaleString('en-IN')}</span>
                      {eligibleTotal < grandTotal && (
                        <div className="text-[10px] text-rose-600 font-bold">Policy Capped Amount: ₹ {eligibleTotal.toLocaleString('en-IN')}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Attachments Section */}
                {r.attachments && r.attachments.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap text-xs bg-white p-2.5 rounded-xl border border-slate-200">
                    <Paperclip className="w-4 h-4 text-slate-400" />
                    <span className="font-bold text-slate-700">Attachments ({r.attachments.length}):</span>
                    {r.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={att.data_url || att.url}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium text-[11px] flex items-center gap-1 border border-blue-200"
                      >
                        <FileText className="w-3 h-3" /> {att.name || `Receipt #${idx + 1}`}
                      </a>
                    ))}
                  </div>
                )}

                {/* Action Bar (When in pending tab & waiting for logged-in user) */}
                {activeTab === 'pending' && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-200">
                    <input
                      type="text"
                      placeholder="Approver remarks / comments..."
                      value={actionRemarks[reportId] || ''}
                      onChange={(e) => handleRemarkChange(reportId, e.target.value)}
                      className="h-9 px-3 bg-white border border-slate-300 rounded-xl text-xs w-full max-w-md outline-none focus:border-blue-500"
                      disabled={isProcessing}
                    />

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleSendBack(reportId)}
                        disabled={isProcessing}
                        className="px-3.5 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all"
                      >
                        Return for Correction
                      </button>
                      <button
                        onClick={() => handleReject(reportId)}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(reportId)}
                        disabled={isProcessing}
                        className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1"
                      >
                        <CheckCircle className="w-4 h-4" /> Approve Claim &rarr;
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <TravelRequestDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        selectedRequestId={selectedReportId}
        selectedModuleType="local_conveyance"
        onActionComplete={fetchInbox}
      />
    </div>
  );

  return isEmbedded ? content : <MainLayout>{content}</MainLayout>;
}
